import React, { useEffect, useState } from "react";
import Card from "components/card";
import {
  MdAdd,
  MdBadge,
  MdCalendarToday,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdDescription,
  MdDirectionsRun,
  MdEdit,
  MdGroup,
  MdLaunch,
  MdPerson,
  MdSave,
} from "react-icons/md";
import { toast, Toaster } from "react-hot-toast";
import { tokenManager } from "utils/tokenManager";

const API_BASE = `${process.env.REACT_APP_PASS_API_BASE || "https://usibras-web-backend.onrender.com"}/api/passes`;

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
  full_name: "",
  person_id: "",
  staff_id: "",
  role: "Staff",
  role_type: "Normal Staff",
  department: "",
  start_date: todayISO(),
  end_date: todayISO(),
  reason: "Official Pass",
  notes: "",
  status: "Granted",
};

const ROLE_OPTIONS = ["Staff", "Manager"];
const ROLE_TYPE_OPTIONS = ["Normal Staff", "Supervisor"];
const STATUS_OPTIONS = ["All", "Pending", "Granted", "Revoked", "Expired"];
const DEPARTMENT_OPTIONS = ["PRODUCTION", "LOGISTICS", "ADMIN", "HR", "QA", "MAINTENANCE"];

const getHeaders = () => {
  const auth = tokenManager.getAuthHeader();
  return {
    "Content-Type": "application/json",
    ...(auth ? { Authorization: auth } : {}),
  };
};

const normalizePass = (item) => ({
  id: item.id,
  full_name: item.full_name ?? item.fullName ?? "",
  person_id: item.person_id ?? item.personId ?? "",
  staff_id: item.staff_id ?? item.staffId ?? "",
  role: item.role ?? "Staff",
  role_type: item.role_type ?? item.roleType ?? "Normal Staff",
  department: item.department ?? "",
  start_date: item.start_date ?? item.startDate ?? "",
  end_date: item.end_date ?? item.endDate ?? "",
  reason: item.reason ?? "",
  notes: item.notes ?? "",
  status: item.status ?? "Granted",
});

const badgeClass = (status) => {
  switch (status) {
    case "Granted":
      return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300";
    case "Pending":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
    case "Revoked":
      return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
    case "Expired":
      return "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-navy-600 dark:text-gray-200";
  }
};

const computePassStats = (items = []) => {
  const totals = {
    total_passes: 0,
    granted_passes: 0,
    pending_passes: 0,
    revoked_passes: 0,
    expired_passes: 0,
    active_passes: 0,
    ended_passes: 0,
  };

  items.forEach((item) => {
    const status = String(item?.status || item?.effective_status || "").trim();
    const effectiveStatus = String(item?.effective_status || "").trim();

    totals.total_passes += 1;

    if (status === "Granted") totals.granted_passes += 1;
    if (status === "Pending") totals.pending_passes += 1;
    if (status === "Revoked") totals.revoked_passes += 1;
    if (status === "Expired") totals.expired_passes += 1;
    if (effectiveStatus === "Active") totals.active_passes += 1;
    if (effectiveStatus === "Ended") totals.ended_passes += 1;
  });

  return totals;
};

const PassCreate = () => {
  const [passes, setPasses] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_passes: 0,
    granted_passes: 0,
    pending_passes: 0,
    revoked_passes: 0,
    expired_passes: 0,
  });
  const [statusFilter, setStatusFilter] = useState("All");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const fetchPasses = async (selectedStatus = statusFilter) => {
    setLoading(true);
    try {
      const url = selectedStatus && selectedStatus !== "All"
        ? `${API_BASE}?status=${encodeURIComponent(selectedStatus)}`
        : API_BASE;
      const res = await fetch(url, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || "Failed to load passes");

      const list = Array.isArray(data?.data) ? data.data : [];
      setPasses(list.map(normalizePass));
      setStats(computePassStats(list));
    } catch (err) {
      toast.error(err.message || "Could not load passes");
    } finally {
      setLoading(false);
    }
  };

  const fetchPassStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/stats`, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || "Failed to load pass statistics");

      const totals = data?.data?.totals || data?.totals || {
        total_passes: 0,
        granted_passes: 0,
        pending_passes: 0,
        revoked_passes: 0,
        expired_passes: 0,
        active_passes: 0,
        ended_passes: 0,
      };

      setStats((prev) => ({ ...prev, ...totals }));
    } catch (err) {
      console.warn(err.message || "Could not load pass statistics");
    }
  };

  const fetchRecipients = async () => {
    try {
      const res = await fetch("https://usibras-web-backend.onrender.com/api/shift-sms/recipients", { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || "Failed to load recipients");
      setRecipients((data?.data || []).map((item) => ({
        id: item.id,
        full_name: item.full_name ?? item.fullName ?? "",
        person_id: item.person_id ?? item.personId ?? "",
        staff_id: item.staff_id ?? item.staffId ?? "",
        role: item.role ?? "Staff",
        role_type: item.role_type ?? item.roleType ?? "Normal Staff",
        department: item.department ?? "",
      })));
    } catch (err) {
      toast.error(err.message || "Could not load recipients");
      setRecipients([]);
    }
  };

  useEffect(() => {
    fetchPasses(statusFilter);
    fetchPassStats();
    fetchRecipients();
  }, []);

  useEffect(() => {
    fetchPasses(statusFilter);
  }, [statusFilter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.full_name.trim()) nextErrors.full_name = "Full name is required";
    if (!form.staff_id.trim()) nextErrors.staff_id = "Staff ID is required";
    if (!form.role) nextErrors.role = "Role is required";
    if (!form.role_type) nextErrors.role_type = "Role type is required";
    if (!form.department) nextErrors.department = "Department is required";
    if (!form.start_date) nextErrors.start_date = "Start date is required";
    if (!form.end_date) nextErrors.end_date = "End date is required";
    if (!form.reason.trim()) nextErrors.reason = "Reason is required";
    if (!form.status) nextErrors.status = "Status is required";
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      nextErrors.end_date = "End date must be on or after the start date";
    }

    return nextErrors;
  };

  const openAddForm = () => {
    setForm(EMPTY_FORM);
    setRecipientSearch("");
    setErrors({});
    setEditingId(null);
    setShowForm(true);
  };

  const applyRecipientDetails = (recipient) => {
    if (!recipient) return;
    setForm((prev) => ({
      ...prev,
      full_name: recipient.full_name || "",
      person_id: recipient.person_id || "",
      staff_id: recipient.staff_id || "",
      role: recipient.role || prev.role || "Staff",
      role_type: recipient.role_type || prev.role_type || "Normal Staff",
      department: recipient.department || "",
    }));
    setRecipientSearch(`${recipient.full_name} (${recipient.staff_id})`);
    setErrors((prev) => ({
      ...prev,
      full_name: "",
      person_id: "",
      staff_id: "",
      role: "",
      role_type: "",
      department: "",
    }));
  };

  const openEditForm = (pass) => {
    setForm({
      full_name: pass.full_name || "",
      person_id: pass.person_id || "",
      staff_id: pass.staff_id || "",
      role: pass.role || "Staff",
      role_type: pass.role_type || "Normal Staff",
      department: pass.department || "",
      start_date: pass.start_date || todayISO(),
      end_date: pass.end_date || todayISO(),
      reason: pass.reason || "",
      notes: pass.notes || "",
      status: pass.status || "Granted",
    });
    setErrors({});
    setEditingId(pass.id);
    setShowForm(true);
  };

  const openDetails = (pass) => {
    setSelectedPass(pass);
    setShowDetails(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.full_name || !form.staff_id || !form.department) {
      toast.error("Please select a recipient before creating a pass.");
      setErrors({
        full_name: !form.full_name ? "Recipient is required" : "",
        staff_id: !form.staff_id ? "Staff ID is required" : "",
        department: !form.department ? "Department is required" : "",
      });
      return;
    }

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload = {
      full_name: form.full_name.trim(),
      person_id: form.person_id.trim(),
      staff_id: form.staff_id.trim(),
      role: form.role,
      role_type: form.role_type,
      department: form.department,
      start_date: form.start_date,
      end_date: form.end_date,
      reason: form.reason.trim(),
      notes: form.notes.trim(),
      status: form.status,
    };

    const method = editingId ? "PUT" : "POST";
    const url = editingId ? `${API_BASE}/${editingId}` : API_BASE;
    const toastId = toast.loading(editingId ? "Updating pass..." : "Creating pass...");

    try {
      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || "Request failed");

      toast.success(data?.message || (editingId ? "Pass updated" : "Pass created"), { id: toastId });
      setShowForm(false);
      setForm(EMPTY_FORM);
      setErrors({});
      setEditingId(null);
      await fetchPasses();
    } catch (err) {
      toast.error(err.message || "Could not save pass", { id: toastId });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pass?")) return;
    const toastId = toast.loading("Deleting pass...");

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || "Delete failed");
      toast.success(data?.message || "Pass deleted", { id: toastId });
      await fetchPasses();
    } catch (err) {
      toast.error(err.message || "Could not delete pass", { id: toastId });
    }
  };

  return (
    <div className="mt-5 h-full space-y-6">
      <Toaster position="top-right" />

      <div className="flex items-center gap-3">
        <MdDirectionsRun className="h-8 w-8 text-brand-500" />
        <div>
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Passes</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Unified pass management using the CRUD API.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
        <Card extra="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500">Total Passes</p>
          <p className="mt-2 text-3xl font-bold text-navy-700 dark:text-white">{stats.total_passes}</p>
        </Card>
        <Card extra="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500">Pending</p>
          <p className="mt-2 text-3xl font-bold text-amber-500">{stats.pending_passes}</p>
        </Card>
        <Card extra="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500">Granted</p>
          <p className="mt-2 text-3xl font-bold text-green-500">{stats.granted_passes}</p>
        </Card>
        <Card extra="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500">Revoked</p>
          <p className="mt-2 text-3xl font-bold text-red-500">{stats.revoked_passes}</p>
        </Card>
        <Card extra="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500">Expired</p>
          <p className="mt-2 text-3xl font-bold text-slate-500">{stats.expired_passes}</p>
        </Card>
      </div>

      <Card extra="p-0 overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-4 dark:border-navy-700 md:flex-row md:items-center md:justify-between">
          <h3 className="text-base font-semibold text-navy-700 dark:text-white">Pass Records</h3>
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 dark:text-gray-300">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={openAddForm}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              <MdAdd className="h-4 w-4" />
              Add Pass
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-navy-700">
            <thead className="bg-gray-50 dark:bg-navy-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Staff ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Department</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Dates</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-navy-700 dark:bg-navy-700">
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400">Loading passes...</td>
                </tr>
              ) : passes.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400">No pass records found.</td>
                </tr>
              ) : (
                passes.map((pass) => (
                  <tr key={pass.id} className="hover:bg-gray-50 dark:hover:bg-navy-600">
                    <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">#{pass.id}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                          <MdPerson className="h-4 w-4 text-brand-500" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-navy-700 dark:text-white">{pass.full_name}</div>
                          <div className="text-xs text-gray-400">{pass.person_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{pass.staff_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{pass.department || "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{pass.role} / {pass.role_type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{pass.reason}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-1 text-xs">
                        <MdCalendarToday className="h-3.5 w-3.5 text-gray-400" />
                        {pass.start_date} → {pass.end_date}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClass(pass.status)}`}>
                        {pass.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openDetails(pass)}
                          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-brand-500 dark:hover:bg-navy-600"
                          title="View details"
                        >
                          <MdLaunch className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditForm(pass)}
                          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-brand-500 dark:hover:bg-navy-600"
                          title="Edit"
                        >
                          <MdEdit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(pass.id)}
                          className="rounded-lg p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                          title="Delete"
                        >
                          <MdDelete className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {showDetails && selectedPass && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-700">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">Pass Details</h3>
              <button
                type="button"
                onClick={() => {
                  setShowDetails(false);
                  setSelectedPass(null);
                }}
                className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-600"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-navy-800">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Pass ID</p>
                  <p className="text-lg font-bold text-navy-700 dark:text-white">#{selectedPass.id}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badgeClass(selectedPass.status)}`}>
                  {selectedPass.status}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Full Name</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.full_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Person ID</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.person_id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Staff ID</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.staff_id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Department</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.department || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Role</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.role}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Role Type</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.role_type}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Start Date</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.start_date}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">End Date</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.end_date}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reason</p>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{selectedPass.reason}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Notes</p>
                <p className="text-sm text-gray-700 dark:text-gray-200">{selectedPass.notes || "No notes provided."}</p>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDetails(false);
                  setSelectedPass(null);
                  openEditForm(selectedPass);
                }}
                className="flex-1 rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white hover:bg-brand-600"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDetails(false);
                  setSelectedPass(null);
                }}
                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-700">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                {editingId ? "Edit Pass" : "Add Pass"}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setErrors({});
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                }}
                className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-600"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!editingId && (
                <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-800/60">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Choose recipient <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      placeholder="Search by name, staff ID, department..."
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    />
                  </div>

                  {recipientSearch.trim() && (
                    <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 dark:border-navy-600 dark:bg-navy-900/40">
                      {recipients.filter((person) => {
                        const q = recipientSearch.trim().toLowerCase();
                        if (!q) return true;
                        return [person.full_name, person.staff_id, person.person_id, person.department, person.role, person.role_type]
                          .filter(Boolean)
                          .some((value) => String(value).toLowerCase().includes(q));
                      }).slice(0, 8).map((person) => (
                        <button
                          key={person.id}
                          type="button"
                          onClick={() => applyRecipientDetails(person)}
                          className="flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2 text-left hover:border-brand-200 hover:bg-brand-50 dark:hover:bg-navy-700"
                        >
                          <div>
                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-100">{person.full_name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{person.staff_id} • {person.department || "No department"}</div>
                          </div>
                          <span className="text-xs text-brand-500">Select</span>
                        </button>
                      ))}
                      {recipients.filter((person) => {
                        const q = recipientSearch.trim().toLowerCase();
                        if (!q) return true;
                        return [person.full_name, person.staff_id, person.person_id, person.department, person.role, person.role_type]
                          .filter(Boolean)
                          .some((value) => String(value).toLowerCase().includes(q));
                      }).length === 0 && (
                        <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No matching recipient found.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="full_name"
                    value={form.full_name}
                    readOnly
                    className={`w-full rounded-lg border bg-gray-100 px-4 py-2.5 text-sm text-gray-700 dark:bg-navy-800 dark:text-white ${errors.full_name ? "border-red-400" : "border-gray-300 dark:border-navy-600"}`}
                  />
                  {errors.full_name && <p className="mt-1 text-xs text-red-500">{errors.full_name}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Person ID</label>
                  <input
                    type="text"
                    name="person_id"
                    value={form.person_id}
                    readOnly
                    className={`w-full rounded-lg border bg-gray-100 px-4 py-2.5 text-sm text-gray-700 dark:bg-navy-800 dark:text-white ${errors.person_id ? "border-red-400" : "border-gray-300 dark:border-navy-600"}`}
                  />
                  {errors.person_id && <p className="mt-1 text-xs text-red-500">{errors.person_id}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Staff ID <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="staff_id"
                    value={form.staff_id}
                    readOnly
                    className={`w-full rounded-lg border bg-gray-100 px-4 py-2.5 text-sm text-gray-700 dark:bg-navy-800 dark:text-white ${errors.staff_id ? "border-red-400" : "border-gray-300 dark:border-navy-600"}`}
                  />
                  {errors.staff_id && <p className="mt-1 text-xs text-red-500">{errors.staff_id}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Role <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={form.role}
                    readOnly
                    className="w-full rounded-lg border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-700 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Role Type <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={form.role_type}
                    readOnly
                    className="w-full rounded-lg border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-700 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Department <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={form.department}
                    readOnly
                    className="w-full rounded-lg border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-700 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="start_date"
                    value={form.start_date}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-white px-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-800 dark:text-white ${errors.start_date ? "border-red-400" : "border-gray-300 dark:border-navy-600"}`}
                  />
                  {errors.start_date && <p className="mt-1 text-xs text-red-500">{errors.start_date}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">End Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="end_date"
                    value={form.end_date}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-white px-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-800 dark:text-white ${errors.end_date ? "border-red-400" : "border-gray-300 dark:border-navy-600"}`}
                  />
                  {errors.end_date && <p className="mt-1 text-xs text-red-500">{errors.end_date}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Reason <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="reason"
                    value={form.reason}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-white px-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-800 dark:text-white ${errors.reason ? "border-red-400" : "border-gray-300 dark:border-navy-600"}`}
                  />
                  {errors.reason && <p className="mt-1 text-xs text-red-500">{errors.reason}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Status <span className="text-red-500">*</span></label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-white px-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-800 dark:text-white ${errors.status ? "border-red-400" : "border-gray-300 dark:border-navy-600"}`}
                  >
                    {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                  {errors.status && <p className="mt-1 text-xs text-red-500">{errors.status}</p>}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setErrors({});
                    setEditingId(null);
                    setForm(EMPTY_FORM);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!form.full_name || !form.staff_id || !form.department}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  <MdSave className="h-4 w-4" />
                  {editingId ? "Save Changes" : "Create Pass"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassCreate;
